import React, { useEffect, useState, Suspense, lazy } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { toast, Toaster } from 'sonner';
import SplitText from './components/SplitText';
import ScrambledText from './components/ScrambledText';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './components/animate-ui/components/radix/dialog';
import { fetchShopItems, fetchRedemptions, fetchChatStats, createWebSocket, fetchLeaderboard, searchLeaderboard, LeaderboardResponse } from './api';
import { ShopItem, Redemption } from './types';
import { useDebounce } from './hooks/use-debounce';

// Lazy load heavy components
const Plasma = lazy(() => import('./components/Plasma'));
const ShopGrid = lazy(() => import('./components/ShopGrid'));
const RedemptionFeed = lazy(() => import('./components/RedemptionFeed'));

function App() {
  const [items, setItems] = useState<ShopItem[]>([]);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [highlightedItem, setHighlightedItem] = useState<string | undefined>(undefined);
  const [chatMessages, setChatMessages] = useState<number>(0);
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse>({ data: [], page: 1, pageSize: 50, totalEntries: 0, totalPages: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const isConnectedRef = React.useRef(false);

  // Check URL on mount for /leaderboard route
  useEffect(() => {
    if (window.location.pathname === '/leaderboard') {
      setIsLeaderboardOpen(true);
    }
  }, []);

  // Initial Data Fetching - Optimized for faster load
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px), (pointer: coarse)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
    } else {
      mq.addListener(update);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', update);
      } else {
        mq.removeListener(update);
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const init = async () => {
      try {
        // Load shop items first (priority)
        const shopItems = await fetchShopItems();
        if (isMounted) {
          setItems(shopItems);
          setLoading(false);
        }
        
        // Load other data in background
        const [pastRedemptions, chatStats] = await Promise.all([
          fetchRedemptions(5),
          fetchChatStats()
        ]);
        
        if (isMounted) {
          setRedemptions(pastRedemptions);
          setChatMessages(chatStats);
          setError(false);
        }
      } catch (error) {
        console.error('Initialization failed:', error);
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    init();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Leaderboard fetching with search and pagination
  useEffect(() => {
    if (!isLeaderboardOpen) return; // Only fetch when leaderboard is open
    
    const loadLeaderboard = async () => {
      setIsSearching(true);
      try {
        let data;
        if (debouncedSearchTerm.trim()) {
          data = await searchLeaderboard(debouncedSearchTerm, currentPage);
        } else {
          data = await fetchLeaderboard(currentPage);
        }
        setLeaderboard(data);
      } catch (error) {
        console.error('Failed to load leaderboard:', error);
      } finally {
        setIsSearching(false);
      }
    };

    loadLeaderboard();
  }, [currentPage, debouncedSearchTerm, isLeaderboardOpen]);

  // WebSocket Connection
  useEffect(() => {
    const handleNewRedemption = async (redemption: Redemption) => {
      setRedemptions(prev => [redemption, ...prev]);

      // Show toast notification only if currently connected
      if (isConnectedRef.current) {
        toast(`${redemption.user_name} Most váltotta ki: ${redemption.item_name}`, {
          style: {
            background: 'white',
            color: 'black',
          },
        });
      }

      // Highlight the item
      setHighlightedItem(redemption.item_name);

      // Remove highlight after 5 seconds
      setTimeout(() => {
        setHighlightedItem(undefined);
      }, 5000);
    };

    const handleChatStats = (count: number) => {
      setChatMessages(count);
    };

    const ws = createWebSocket(
      handleNewRedemption,
      handleChatStats,
      () => {
        setConnected(true);
        isConnectedRef.current = true;
      },
      () => {
        setConnected(false);
        isConnectedRef.current = false;
      }
    );

    return () => {
      // Properly cleanup WebSocket without type casting
      ws.onmessage = null;
      ws.onerror = null;
      ws.onopen = null;
      ws.onclose = null;
      ws.close();
    };
  }, []);

  return (
    <div className="relative min-h-screen text-foreground overflow-hidden flex flex-col">
      {/* Toast Container */}
      <Toaster position="bottom-right" />
      
      {/* Background */}
      <div style={{ width: '100%', height: '100%', position: 'fixed', top: 0, left: 0, zIndex: -1 }}>
        {isMobile ? (
          <div style={{ width: '100%', height: '100%', backgroundColor: '#0b0b0b' }} />
        ) : (
          <Suspense fallback={<div />}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              style={{ width: '100%', height: '100%' }}
            >
              <Plasma
                color="#ffffff"
                speed={0.5}
                direction="forward"
                scale={1}
                opacity={0.25}
                mouseInteractive={false}
              />
            </motion.div>
          </Suspense>
        )}
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-6 md:px-4 pt-8 flex-grow flex flex-col">

        {/* Header with buttons */}
        <div className="absolute top-4 left-4 md:top-8 md:left-8 z-20">
          <button
            onClick={() => window.open('https://botrix.live/k/suri/shop', '_blank')}
            className="px-4 py-1.5 md:px-8 md:py-2 text-sm md:text-base bg-white text-black font-semibold rounded-full hover:bg-white/90 transition"
          >
            Shop
          </button>
        </div>

        <div className="absolute top-4 right-4 md:top-8 md:right-8 z-20">
          <Dialog open={isLeaderboardOpen} onOpenChange={(open) => {
            setIsLeaderboardOpen(open);
            // Delay URL update to prevent flash during animation
            if (open) {
              window.history.pushState({}, '', '/leaderboard');
            } else {
              setTimeout(() => {
                window.history.pushState({}, '', '/');
              }, 300); // Wait for dialog close animation
              // Reset search when closing
              setSearchTerm('');
              setCurrentPage(1);
            }
          }}>
            <DialogTrigger asChild>
              <button className="px-4 py-1.5 md:px-8 md:py-2 text-sm md:text-base bg-white text-black font-semibold rounded-full hover:bg-white/90 transition">
                Leaderboard
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl flex flex-col gap-4 h-[80vh] rounded-2xl bg-black/95 border border-white/10" style={{ WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}>
              <DialogHeader>
                <DialogTitle>Leaderboard</DialogTitle>
              </DialogHeader>
              
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Keresés név alapján..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white/80 rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              
              {/* Leaderboard List */}
              <div className="overflow-y-auto pr-2 flex-1">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={currentPage}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="space-y-2"
                    style={{ 
                      WebkitFontSmoothing: 'subpixel-antialiased',
                      MozOsxFontSmoothing: 'auto',
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden',
                      perspective: 1000
                    }}
                  >
                    {leaderboard.data.length > 0 ? (
                      leaderboard.data.map((entry, index) => {
                        const displayRank = entry.rank || ((currentPage - 1) * leaderboard.pageSize + index + 1);
                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-white/60 font-semibold w-10">#{displayRank}</span>
                              <span className="font-semibold">{entry.name}</span>
                            </div>
                            <span className="text-white/80 font-semibold">{entry.points.toLocaleString()}</span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-white/60 text-center py-8">
                        {searchTerm ? 'Nincs találat' : 'Nincs leaderboard adat'}
                      </p>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
              
              {/* Pagination */}
              {leaderboard.totalPages > 1 && (
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    ← Előző
                  </button>
                  
                  <div className="flex items-center gap-2">
                    {/* First page */}
                    {currentPage > 2 && (
                      <>
                        <button
                          onClick={() => setCurrentPage(1)}
                          className="px-3 py-1 bg-white/5 rounded-lg hover:bg-white/10 transition"
                        >
                          1
                        </button>
                        {currentPage > 3 && <span className="text-white/40">...</span>}
                      </>
                    )}
                    
                    {/* Previous page */}
                    {currentPage > 1 && (
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        className="px-3 py-1 bg-white/5 rounded-lg hover:bg-white/10 transition"
                      >
                        {currentPage - 1}
                      </button>
                    )}
                    
                    {/* Current page */}
                    <button className="px-3 py-1 bg-white/20 rounded-lg font-semibold">
                      {currentPage}
                    </button>
                    
                    {/* Next page */}
                    {currentPage < leaderboard.totalPages && (
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        className="px-3 py-1 bg-white/5 rounded-lg hover:bg-white/10 transition"
                      >
                        {currentPage + 1}
                      </button>
                    )}
                    
                    {/* Last page */}
                    {currentPage < leaderboard.totalPages - 1 && (
                      <>
                        {currentPage < leaderboard.totalPages - 2 && <span className="text-white/40">...</span>}
                        <button
                          onClick={() => setCurrentPage(leaderboard.totalPages)}
                          className="px-3 py-1 bg-white/5 rounded-lg hover:bg-white/10 transition"
                        >
                          {leaderboard.totalPages}
                        </button>
                      </>
                    )}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(leaderboard.totalPages, prev + 1))}
                    disabled={currentPage === leaderboard.totalPages}
                    className="px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    Következő →
                  </button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Header */}
        <header className="mb-8 md:mb-12 text-center pt-12 md:pt-0">
          <SplitText
            text="Kick Shop Monitor"
            duration={0.8}
            className="text-3xl sm:text-4xl md:text-6xl font-bold mb-3 md:mb-4 tracking-tighter"
          />
          <p className="text-base sm:text-lg md:text-xl text-white/60 px-4">
            Suri store kiváltások tracker
          </p>
          <p className="text-xs sm:text-sm text-white/40 mt-2 px-4">
            A tracker nem 100%-ban pontos.
          </p>
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-10 gap-6 md:gap-8 max-w-[2000px] mx-auto">

          {/* Left Column: Shop Grid (Takes up 7/10 space on large screens) */}
          <div className="xl:col-span-7">
            {!loading && items.length > 0 && (
              <div className="mb-4 md:mb-6">
                <h2 className="text-xl md:text-2xl font-semibold">itemek</h2>
              </div>
            )}
            {error ? (
              <div className="flex flex-col items-center justify-center py-16 space-y-4">
                <ScrambledText className="text-2xl md:text-4xl font-bold text-red-400">
                  Adatok betöltése sikertelen
                </ScrambledText>
                <ScrambledText className="text-lg md:text-2xl text-white/60">
                  Szerverhez nem sikerült kapcsolódni
                </ScrambledText>
              </div>
            ) : (
              <Suspense fallback={<div />}>
                <ShopGrid
                  items={items}
                  loading={loading}
                  highlightedItem={highlightedItem}
                />
              </Suspense>
            )}
          </div>

          {/* Right Column: Live Feed (Takes up 3/10 space on large screens) */}
          <div className="xl:col-span-3">
            {/* Spacer to align with items header */}
            {!loading && items.length > 0 && (
              <div className="mb-4 md:mb-6 h-7 md:h-8" />
            )}
            <div className="xl:sticky xl:top-8">
              <Suspense fallback={<div />}>
                <RedemptionFeed
                  redemptions={redemptions}
                  connected={connected}
                  chatMessages={chatMessages}
                />
              </Suspense>
            </div>
          </div>

        </div>

        {/* Footer */}
        <footer className="mt-auto pt-6 pb-2 text-center">
          <p className="text-xs text-white/30">ai generated site</p>
        </footer>
      </div>
    </div>
  );
}

export default App;
